import React, { useState, useRef, useCallback, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./style.css";

// ── Image Modal ───────────────────────────────────────────────────────────────
const ImageModal = ({ onClose, onUrl, onFile }) => {
  const [url, setUrl] = useState("");
  const fileRef = useRef(null);
  return (
    <div className="img-backdrop" onClick={onClose}>
      <div className="img-modal" onClick={(e) => e.stopPropagation()}>
        <div className="img-modal-header">
          <span>Insert Image</span>
          <button className="img-close" onClick={onClose}>✕</button>
        </div>
        <div className="img-modal-body">
          <label className="img-label">From URL</label>
          <div className="img-url-row">
            <input
              autoFocus
              type="url"
              className="img-input"
              placeholder="https://example.com/image.jpg"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onUrl(url); }}
            />
            <button className="img-btn img-btn--primary" onClick={() => onUrl(url)}>Insert</button>
          </div>
          <div className="img-divider"><span>or</span></div>
          <button className="img-upload-btn" onClick={() => fileRef.current?.click()}>
            <span className="img-upload-icon">📁</span> Upload from device
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={(e) => onFile(e.target.files[0])} />
        </div>
      </div>
    </div>
  );
};

// ── Editor ────────────────────────────────────────────────────────────────────
const Editor = ({ value, onChange }) => {
  const quillRef = useRef(null);
  const rangeRef = useRef(null);
  const [imgModal, setImgModal] = useState(false);

  const imageHandler = useCallback(() => {
    rangeRef.current = quillRef.current?.getEditor().getSelection(true);
    setImgModal(true);
  }, []);

  const doInsertUrl = useCallback((url) => {
    if (!url) return;
    const editor = quillRef.current?.getEditor();
    const idx = rangeRef.current?.index ?? editor.getLength();
    editor.insertEmbed(idx, "image", url);
    editor.setSelection(idx + 1);
    setImgModal(false);
  }, []);

  const doInsertFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => doInsertUrl(e.target.result);
    reader.readAsDataURL(file);
  }, [doInsertUrl]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        ["link", "image"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
    history: { delay: 400, maxStack: 200, userOnly: true },
  }), [imageHandler]);

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "color", "background", "align", "list", "bullet",
    "blockquote", "code-block", "link", "image",
  ];

  return (
    <>
      <div className="rte-quill-wrap">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder="Write your newsletter content here…"
        />
      </div>
      {imgModal && (
        <ImageModal
          onClose={() => setImgModal(false)}
          onUrl={doInsertUrl}
          onFile={doInsertFile}
        />
      )}
    </>
  );
};

const readApiBody = async (res) => {
  try {
    return await res.json();
  } catch {
    return {};
  }
};

const adminErrorMessage = (status, error, fallback) => {
  if (status === 401) return "비밀번호가 틀렸어요.";
  if (error === "ADMIN_PASSWORD is not configured") {
    return "서버에 ADMIN_PASSWORD가 설정되어 있지 않아요.";
  }
  if (status >= 500) return `${fallback} (${error || "서버 설정 오류"})`;
  return error || fallback;
};

// ── Admin ─────────────────────────────────────────────────────────────────────
export const Admin = () => {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscriberError, setSubscriberError] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sendStatus, setSendStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testStatus, setTestStatus] = useState("");

  const loadSubscribers = useCallback(async (adminPassword = password) => {
    setSubscribersLoading(true);
    setSubscriberError("");
    try {
      const res = await fetch("/api/admin/subscribers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: adminPassword.trim() }),
      });
      const data = await readApiBody(res);
      if (res.ok) {
        setSubscribers(data.subscribers || []);
      } else {
        setSubscriberError(adminErrorMessage(res.status, data.error, "구독자 목록을 불러오지 못했어요."));
      }
    } catch {
      setSubscriberError("구독자 목록을 불러오지 못했어요. (서버 연결 오류)");
    }
    setSubscribersLoading(false);
  }, [password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const loginPassword = password.trim();
    if (!loginPassword) {
      setAuthError("비밀번호를 입력해 주세요.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: loginPassword }),
      });
      const data = await readApiBody(res);
      if (res.ok) {
        setPassword(loginPassword);
        setAuthed(true);
        setAuthError("");
        loadSubscribers(loginPassword);
      } else {
        setAuthError(adminErrorMessage(res.status, data.error, "로그인에 실패했어요."));
      }
    } catch { setAuthError("서버 오류가 발생했어요."); }
    setLoading(false);
  };

  const handleLoadDraft = async () => {
    setDraftLoading(true);
    try {
      const res = await fetch("/api/admin/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: password.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.draft) {
        setSubject(data.draft.subject);
        const asHtml = data.draft.content.split("\n").map((line) => {
          if (line.startsWith("## ")) return `<h2>${line.slice(3)}</h2>`;
          if (line.startsWith("- ")) return `<ul><li>${line.slice(2)}</li></ul>`;
          if (line === "") return "<p><br></p>";
          return `<p>${line}</p>`;
        }).join("");
        setContent(asHtml);
      } else {
        alert(data.draft === null ? "저장된 드래프트가 없어요." : "드래프트 불러오기 실패");
      }
    } catch { alert("서버 오류가 발생했어요."); }
    setDraftLoading(false);
  };

  const handleTestSend = async () => {
    if (!testEmail || !subject || !content) return;
    const testList = testEmail.split(",").map((e) => e.trim()).filter(Boolean);
    setTestStatus("sending");
    try {
      const res = await fetch("/api/admin/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: password.trim(), subject: `[테스트] ${subject}`, content, contentType: "html", subscribers: testList, archive: false }),
      });
      const data = await res.json();
      setTestStatus(res.ok ? "success" : `error:${data.error}`);
    } catch { setTestStatus("error:서버 오류"); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject || !content) return;
    setSendStatus("sending");
    try {
      const res = await fetch("/api/admin/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: password.trim(), subject, content, contentType: "html", subscribers: subscribers.map((s) => s.email), archive: true }),
      });
      const data = await res.json();
      if (res.ok) {
        setSendStatus(`success:${data.sent}`);
        setSubject("");
        setContent("");
      } else {
        setSendStatus(`error:${data.error}`);
      }
    } catch { setSendStatus("error:서버 오류"); }
  };

  if (!authed) {
    return (
      <div className="admin-login">
        <div className="admin-login-box">
          <img src="/image/rosota_smalllogo.svg" alt="rosota" className="admin-logo" />
          <h2>Admin</h2>
          <form onSubmit={handleLogin}>
            <input type="password" placeholder="비밀번호" value={password}
              onChange={(e) => setPassword(e.target.value)} className="admin-input" required />
            {authError && <p className="admin-error">{authError}</p>}
            <button type="submit" className="admin-btn" disabled={loading}>
              {loading ? "…" : "로그인"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <img src="/image/rosota_smalllogo.svg" alt="rosota" className="admin-logo" />
        <h2>Newsletter Admin</h2>
      </div>

      <div className="admin-section">
        <h3>구독자 <span className="admin-count">{subscribers.length}명</span></h3>
        {subscribersLoading && <p className="admin-success">구독자 목록을 불러오는 중…</p>}
        {subscriberError && <p className="admin-error">{subscriberError}</p>}
        <div className="admin-subscriber-list">
          {subscribers.map((s, i) => (
            <div key={i} className="admin-subscriber-item">
              <span className="admin-subscriber-email">{s.email}</span>
              <span className="admin-subscriber-date">{s.date}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-section">
        <h3>뉴스레터 발송</h3>
        <div className="admin-draft-row">
          <button type="button" className="admin-btn-outline" onClick={handleLoadDraft} disabled={draftLoading}>
            {draftLoading ? "불러오는 중…" : "✦ AI 드래프트 불러오기"}
          </button>
          <span className="admin-draft-hint">매일 오전 8시 자동생성됩니다</span>
        </div>

        <form onSubmit={handleSend} className="admin-compose">
          <input type="text" placeholder="제목 (Subject)" value={subject}
            onChange={(e) => setSubject(e.target.value)} className="admin-input admin-subject-input" required />

          <Editor value={content} onChange={setContent} />

          <div className="admin-test-row">
            <input type="email" placeholder="테스트 이메일 (쉼표로 여러 개)" value={testEmail}
              onChange={(e) => { setTestEmail(e.target.value); setTestStatus(""); }}
              className="admin-input admin-input-inline" />
            <button type="button" className="admin-btn-outline" onClick={handleTestSend}
              disabled={testStatus === "sending" || !testEmail}>
              {testStatus === "sending" ? "발송 중…" : "테스트 발송"}
            </button>
            {testStatus === "success" && <span className="admin-success-inline">✓ 발송됨</span>}
            {testStatus.startsWith("error") && <span className="admin-error-inline">오류</span>}
          </div>

          <div className="admin-send-row">
            <span className="admin-send-info">수신자 {subscribers.length}명</span>
            <button type="submit" className="admin-btn" disabled={sendStatus === "sending"}>
              {sendStatus === "sending" ? "발송 중…" : "전체 발송 →"}
            </button>
          </div>
          {sendStatus.startsWith("success") && <p className="admin-success">✓ {sendStatus.split(":")[1]}명에게 발송 완료</p>}
          {sendStatus.startsWith("error") && <p className="admin-error">오류: {sendStatus.split(":")[1]}</p>}
        </form>
      </div>
    </div>
  );
};
